import { useEffect, useState, useMemo, useRef } from "react";
import {
  Modal,
  TextField,
  ResourceList,
  Text,
  Checkbox,
  InlineStack,
  BlockStack,
  Spinner,
  Box,
  ChoiceList,
  Button,
} from "@medentic/polaris-react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ErrorResponse } from "@shared/types/commonTypes";
import { TranslateLink } from "@shared/ui";
import { Payment, Attendee } from "@features/events/types";

interface AssignTicketsModalProps {
  open: boolean;
  onClose: () => void;
  payment: Payment | null;
  onSuccess: (multi: boolean, names?: string[]) => void;
}

interface TicketTypeGroup {
  ticketId: string;
  title: string;
  count: number;
}

const AssignTicketsModal = ({
  open,
  onClose,
  payment,
  onSuccess,
}: AssignTicketsModalProps) => {
  const { t } = useTranslation();
  const { GET, POST } = useApiRequest();
  const getRef = useRef(GET);
  const [allAttendees, setAllAttendees] = useState<Attendee[]>([]);
  const [filteredAttendees, setFilteredAttendees] = useState<Attendee[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notify, setNotify] = useState(true);
  const [selectedTicketType, setSelectedTicketType] = useState<string | null>(
    null
  );
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();

  useEffect(() => {
    getRef.current = GET;
  }, [GET]);

  // Get unique ticket types from unassigned tickets
  const uniqueTicketTypes = useMemo<TicketTypeGroup[]>(() => {
    const unassigned = payment?.assignedTickets?.filter((t) => !t.taken) || [];
    const typeMap = new Map<
      string,
      { ticketId: string; title: string; count: number }
    >();
    unassigned.forEach((t) => {
      const existing = typeMap.get(t.ticketId);
      if (existing) {
        existing.count++;
      } else {
        typeMap.set(t.ticketId, {
          ticketId: t.ticketId,
          title: t.title,
          count: 1,
        });
      }
    });
    return Array.from(typeMap.values());
  }, [payment?.assignedTickets]);

  const hasMultipleTicketTypes = uniqueTicketTypes.length > 1;

  // Auto-select if only one type
  useEffect(() => {
    if (uniqueTicketTypes.length === 1) {
      setSelectedTicketType(uniqueTicketTypes[0].ticketId);
    } else if (uniqueTicketTypes.length === 0) {
      setSelectedTicketType(null);
    }
  }, [uniqueTicketTypes]);

  // Get max selectable attendees for current ticket type
  const maxSelectableAttendees = useMemo(() => {
    if (!selectedTicketType || !payment?.assignedTickets) return 0;
    return payment.assignedTickets.filter(
      (t) => t.ticketId === selectedTicketType && !t.taken
    ).length;
  }, [selectedTicketType, payment?.assignedTickets]);

  // Handler to change ticket type - clears selected attendees
  const handleTicketTypeChange = (ticketId: string | null) => {
    setSelectedTicketType(ticketId);
    setSelected([]);
    setError(null);
  };

  // Filter attendees in memory based on search
  useEffect(() => {
    if (!searchInput.trim()) {
      setFilteredAttendees(allAttendees);
    } else {
      const searchLower = searchInput.toLowerCase();
      const filtered = allAttendees.filter(
        (attendee) =>
          attendee.firstName.toLowerCase().includes(searchLower) ||
          attendee.lastName.toLowerCase().includes(searchLower) ||
          attendee.email.toLowerCase().includes(searchLower)
      );
      setFilteredAttendees(filtered);
    }
  }, [searchInput, allAttendees]);

  useEffect(() => {
    if (open && payment) {
      setLoading(true);
      setError(null);
      getRef.current(
        `/api/healthcare-companies/${hcc}/e/attendees?notInEventIds=${payment.eventId}`
      )
        .then((data) => {
          setAllAttendees(data as Attendee[]);
          setFilteredAttendees(data as Attendee[]);
        })
        .catch(() => setError(t("eventTransactions.failedToLoadAttendees")))
        .finally(() => setLoading(false));
    }
  }, [open, payment, hcc, t]);

  useEffect(() => {
    if (!open) {
      setAllAttendees([]);
      setFilteredAttendees([]);
      setSelected([]);
      setSearchInput("");
      setError(null);
      setNotify(true);
      setSelectedTicketType(null);
    }
  }, [open]);

  const handleAssign = async () => {
    if (!payment || selected.length === 0 || !selectedTicketType) return;
    setAssigning(true);
    setError(null);
    const successNames: string[] = [];
    try {
      for (const attId of selected) {
        const attendee = allAttendees.find((a) => a.id === attId);
        await POST(
          `/api/healthcare-companies/${hcc}/e/attendees/${attId}/ticket`,
          {
            eventId: payment.eventId,
            ticketId: selectedTicketType,
            paymentId: payment.id,
            notify: notify,
          }
        );
        if (attendee)
          successNames.push(`${attendee.firstName} ${attendee.lastName}`);
      }
      onSuccess(successNames.length > 1, successNames);

      onClose();
    } catch (e: unknown) {
      const displayMsg = (e as ErrorResponse)?.response?.data?.error?.display;
      setError(displayMsg || t("eventTransactions.failedToAssignTickets"));
    } finally {
      setAssigning(false);
    }
  };

  const handleSelect = (id: string) => {
    setError(null);
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((s) => s !== id)
        : prev.length < maxSelectableAttendees
        ? [...prev, id]
        : prev
    );
  };

  // Get the selected ticket type info for display
  const selectedTicketTypeInfo = uniqueTicketTypes.find(
    (t) => t.ticketId === selectedTicketType
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("eventTransactions.assignTicketsToAttendees")}
      primaryAction={{
        content: t("eventTransactions.assignTickets"),
        onAction: handleAssign,
        loading: assigning,
        disabled: selected.length === 0 || !selectedTicketType,
      }}
      secondaryActions={[
        { content: t("common.cancel"), onAction: onClose, disabled: assigning },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="200">
          {/* Step 1: Ticket Type Selection (only if multiple types) */}
          {hasMultipleTicketTypes && !selectedTicketType && (
            <BlockStack gap="300">
              <Text as="p">{t("eventTransactions.selectTicketTypeToAssign")}</Text>
              <ChoiceList
                title=""
                titleHidden
                choices={uniqueTicketTypes.map((type) => ({
                  label: `${type.title} (${type.count} ${t("eventTransactions.unassigned")})`,
                  value: type.ticketId,
                }))}
                selected={selectedTicketType ? [selectedTicketType] : []}
                onChange={(values) => handleTicketTypeChange(values[0] || null)}
              />
            </BlockStack>
          )}

          {/* Step 2: Attendee Selection (when ticket type is selected) */}
          {selectedTicketType && (
            <>
              {hasMultipleTicketTypes && (
                <Box paddingBlockEnd="300">
                  <InlineStack gap="200" align="space-between">
                    <Text as="p" fontWeight="semibold">
                      {t("eventTransactions.assigningTo")}:{" "}
                      {selectedTicketTypeInfo?.title}
                    </Text>
                    <Button
                      variant="plain"
                      onClick={() => handleTicketTypeChange(null)}
                    >
                      {t("common.change")}
                    </Button>
                  </InlineStack>
                </Box>
              )}
              <TranslateLink
                text={t("eventTransactions.assignTicketsDescription", {
                  count: maxSelectableAttendees,
                  eventName: payment?.eventTitle,
                  ticketName: selectedTicketTypeInfo?.title || "",
                })}
              ></TranslateLink>
              <Checkbox
                label={t("eventTransactions.sendAttendeeEmails")}
                checked={notify}
                onChange={setNotify}
              />
              <TextField
                label={""}
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t("eventTransactions.searchByNameOrEmail")}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setSearchInput("")}
              />
              {loading ? (
                <Box padding="400">
                  <InlineStack gap="200" blockAlign="center" align="center">
                    <Spinner
                      accessibilityLabel={t("eventTransactions.loadingAttendees")}
                      size="small"
                    />
                  </InlineStack>
                </Box>
              ) : (
                <>
                  {error && (
                    <Text as="p" tone="critical">
                      {error}
                    </Text>
                  )}

                  <ResourceList
                    resourceName={{
                      singular: t("eventTransactions.attendee"),
                      plural: t("eventTransactions.attendees"),
                    }}
                    items={filteredAttendees}
                    renderItem={(att) => {
                      const {
                        id,
                        firstName,
                        lastName,
                        email,
                        phoneCountryCode,
                        phoneNumber,
                        totalVisitedEvents,
                      } = att;
                      return (
                        <ResourceList.Item
                          id={id}
                          onClick={() => handleSelect(id)}
                        >
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="200" blockAlign="center">
                              <Checkbox
                                label=""
                                checked={selected.includes(id)}
                                onChange={() => handleSelect(id)}
                                disabled={
                                  !selected.includes(id) &&
                                  selected.length >= maxSelectableAttendees
                                }
                              />
                              <BlockStack gap="050">
                                <Text as="span" fontWeight="bold">
                                  {firstName} {lastName}
                                </Text>
                                <Text as="span" tone="subdued">
                                  {email}
                                </Text>
                                {phoneCountryCode && phoneNumber && (
                                  <Text as="span" tone="subdued">
                                    {phoneCountryCode} {phoneNumber}
                                  </Text>
                                )}
                              </BlockStack>
                            </InlineStack>
                            {totalVisitedEvents === 0 ? (
                              <Text as="span" tone="subdued">
                                <em>
                                  {t("eventTransactions.hasntAttendedAnyEvents")}
                                </em>
                              </Text>
                            ) : (
                              <Text as="span">
                                {totalVisitedEvents === 1
                                  ? t("eventTransactions.attendedOneEvent")
                                  : t("eventTransactions.attendedXEvents", {
                                      count: totalVisitedEvents,
                                    })}
                              </Text>
                            )}
                          </InlineStack>
                        </ResourceList.Item>
                      );
                    }}
                  />
                </>
              )}
            </>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default AssignTicketsModal;
